// https://www.iana.org/assignments/icmpv6-parameters/icmpv6-parameters.xhtml

/*
Cisco ASA
  echo                    	type 128
  echo-reply              	type 129
  membership-query        	type 130
  membership-reduction    	type 132
  membership-report       	type 131
  neighbor-advertisement	type 136
  neighbor-redirect		type 137
  neighbor-solicitation		type 135
  packet-too-big          	type 2
  parameter-problem       	type 4
  router-advertisement    	type 134
  router-renumbering      	type 138
  router-solicitation     	type 133
  time-exceeded           	type 3
  unreachable             	type 1

*/

export type Icmp6TypesCode = {
  type: number;
  code: number;
  name: string;
  keys: string[];
};

export function icmp6TypesCodes(): Icmp6TypesCode[] {
  return [
    { type: 1, code: 0, name: 'no route to destination', keys: [] },
    {
      type: 1,
      code: 1,
      name: 'communication with destination administratively prohibited',
      keys: [],
    },
    {
      type: 1,
      code: 2,
      name: 'beyond scope of source address',
      keys: ['beyond-scope'],
    },
    {
      type: 1,
      code: 3,
      name: 'address unreachable',
      keys: ['destination-unreachable'],
    },
    { type: 1, code: 4, name: 'port unreachable', keys: [] },
    {
      type: 1,
      code: 5,
      name: 'source address failed ingress/egress policy',
      keys: [],
    },
    { type: 1, code: 6, name: 'reject route to destination', keys: [] },
    { type: 1, code: 7, name: 'Error in Source Routing Header', keys: [] },
    { type: 2, code: 0, name: 'Packet Too Big', keys: ['packet-too-big'] }, // no specific code description (type name taken)
    {
      type: 3,
      code: 0,
      name: 'hop limit exceeded in transit',
      keys: ['hop-limit'],
    },
    {
      type: 3,
      code: 1,
      name: 'fragment reassembly time exceeded',
      keys: ['reassembly-timeout'],
    },
    { type: 4, code: 0, name: 'erroneous header field encountered', keys: [] },
    {
      type: 4,
      code: 1,
      name: 'unrecognized Next Header type encountered',
      keys: [],
    },
    {
      type: 4,
      code: 2,
      name: 'unrecognized IPv6 option encountered',
      keys: [],
    },
    {
      type: 4,
      code: 3,
      name: 'IPv6 First Fragment has incomplete IPv6 Header Chain',
      keys: [],
    },
    {
      type: 128,
      code: 0,
      name: 'Echo Request',
      keys: ['echo', 'echo-request'],
    }, // no specific code description (type name taken)
    { type: 129, code: 0, name: 'Echo Reply', keys: ['echo-reply'] }, // no specific code description (type name taken)
    {
      type: 130,
      code: 0,
      name: 'Multicast Listener Query',
      keys: ['membership-query', 'mld-query'],
    }, // no specific code description (type name taken)
    {
      type: 131,
      code: 0,
      name: 'Multicast Listener Report',
      keys: ['membership-report', 'mld-report'],
    }, // no specific code description (type name taken)
    {
      type: 132,
      code: 0,
      name: 'Multicast Listener Done',
      keys: ['membership-reduction', 'mld-reduction'],
    }, // no specific code description (type name taken)
    {
      type: 133,
      code: 0,
      name: 'Router Solicitation',
      keys: ['router-solicitation'],
    }, // no specific code description (type name taken)
    {
      type: 134,
      code: 0,
      name: 'Router Advertisement',
      keys: ['router-advertisement'],
    }, // no specific code description (type name taken)
    {
      type: 135,
      code: 0,
      name: 'Neighbor Solicitation',
      keys: ['neighbor-solicitation', 'nd-ns'],
    }, // no specific code description (type name taken)
    {
      type: 136,
      code: 0,
      name: 'Neighbor Advertisement',
      keys: ['neighbor-advertisement', 'nd-na'],
    }, // no specific code description (type name taken)
    {
      type: 137,
      code: 0,
      name: 'Redirect Message',
      keys: ['neighbor-redirect'],
    }, // no specific code description (type name taken)
    {
      type: 138,
      code: 0,
      name: 'Router Renumbering Command',
      keys: ['router-renumbering'],
    },
    { type: 138, code: 1, name: 'Router Renumbering Result', keys: [] },
    { type: 138, code: 255, name: 'Sequence Number Reset', keys: [] },
    {
      type: 139,
      code: 0,
      name: 'The Data field contains an IPv6 address which is the Subject of this Query.',
      keys: [],
    },
    {
      type: 139,
      code: 1,
      name: 'The Data field contains a name which is the Subject of this Query, or is empty, as in the case of a NOOP.',
      keys: [],
    },
    {
      type: 139,
      code: 2,
      name: 'The Data field contains an IPv4 address which is the Subject of this Query.',
      keys: [],
    },
    {
      type: 140,
      code: 0,
      name: 'A successful reply. The Reply Data field may or may not be empty.',
      keys: [],
    },
    {
      type: 140,
      code: 1,
      name: 'The Responder refuses to supply the answer. The Reply Data field will be empty.',
      keys: [],
    },
    {
      type: 140,
      code: 2,
      name: 'The Qtype of the Query is unknown to the Responder. The Reply Data field will be empty.',
      keys: [],
    },
    {
      type: 141,
      code: 0,
      name: 'Inverse Neighbor Discovery Solicitation Message',
      keys: [],
    }, // no specific code description (type name taken)
    {
      type: 142,
      code: 0,
      name: 'Inverse Neighbor Discovery Advertisement Message',
      keys: [],
    }, // no specific code description (type name taken)
    {
      type: 143,
      code: 0,
      name: 'Multicast Listener Discovery (MLDv2) reports',
      keys: ['mldv2'],
    },
    {
      type: 144,
      code: 0,
      name: 'Home Agent Address Discovery Request Message',
      keys: ['dhaad-request'],
    }, // no specific code description (type name taken)
    {
      type: 145,
      code: 0,
      name: 'Home Agent Address Discovery Reply Message',
      keys: ['dhaad-reply'],
    }, // no specific code description (type name taken)
    {
      type: 146,
      code: 0,
      name: 'Mobile Prefix Solicitation',
      keys: ['mpd-solicitation'],
    }, // no specific code description (type name taken)
    {
      type: 147,
      code: 0,
      name: 'Mobile Prefix Advertisement',
      keys: ['mpd-advertisement'],
    }, // no specific code description (type name taken)
    { type: 157, code: 0, name: 'DAR message', keys: [] },
    {
      type: 157,
      code: 1,
      name: 'EDAR message with 64-bit ROVR field',
      keys: [],
    },
    {
      type: 157,
      code: 2,
      name: 'EDAR message with 128-bit ROVR field',
      keys: [],
    },
    {
      type: 157,
      code: 3,
      name: 'EDAR message with 192-bit ROVR field',
      keys: [],
    },
    {
      type: 157,
      code: 4,
      name: 'EDAR message with 256-bit ROVR field',
      keys: [],
    },
    // {type: 157, code: 5-15, name: 'Unassigned', keys: []},
    { type: 158, code: 0, name: 'DAC message', keys: [] },
    {
      type: 158,
      code: 1,
      name: 'EDAC message with 64-bit ROVR field',
      keys: [],
    },
    {
      type: 158,
      code: 2,
      name: 'EDAC message with 128-bit ROVR field',
      keys: [],
    },
    {
      type: 158,
      code: 3,
      name: 'EDAC message with 192-bit ROVR field',
      keys: [],
    },
    {
      type: 158,
      code: 4,
      name: 'EDAC message with 256-bit ROVR field',
      keys: [],
    },
    // {type: 158, code: 5-15, name: 'Unassigned', keys: []},
    { type: 160, code: 0, name: 'No Error', keys: [] },
    // {type: 160, code: 1-255, name: 'Unassigned', keys: []},
    { type: 161, code: 0, name: 'No Error', keys: [] },
    { type: 161, code: 1, name: 'Malformed Query', keys: [] },
    { type: 161, code: 2, name: 'No Such Interface', keys: [] },
    { type: 161, code: 3, name: 'No Such Table Entry', keys: [] },
    { type: 161, code: 4, name: 'Multiple Interfaces Satisfy Query', keys: [] },
    // {type: 161, code: 5-255, name: 'Unassigned', keys: []},
  ];
}
